import { useState, useEffect, useMemo } from "react";
import { Search } from "lucide-react";
import { Modal } from "../primitives/Modal";
import { Button } from "../primitives/Button";
import { createMembership, listAdminUsers } from "../../api/admin";
import { ROLE_OPTIONS } from "../../api/admin";
import { getRoleDescription } from "../../utils/permissions";
import { cn } from "../utils/cn";

type AddMemberToOrgModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  organizationId: string;
};

export function AddMemberToOrgModal({ open, onClose, onSuccess, organizationId }: AddMemberToOrgModalProps) {
  const [users, setUsers] = useState<Array<{ id: string; email: string; first_name?: string; last_name?: string; display_name?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [role, setRole] = useState<string>("ops");
  const [status, setStatus] = useState<"active" | "suspended">("active");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLoading(true);
      listAdminUsers({ page_size: 200 })
        .then((data) => {
          setUsers(data.items || []);
        })
        .catch((error) => {
          console.error("Failed to load users:", error);
          setError("Erreur lors du chargement des utilisateurs");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSelectedUserId("");
      setRole("operator");
      setStatus("active");
      setError(null);
    }
  }, [open]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.email.toLowerCase().includes(query) ||
        user.first_name?.toLowerCase().includes(query) ||
        user.last_name?.toLowerCase().includes(query) ||
        user.display_name?.toLowerCase().includes(query) ||
        `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const getUserDisplayName = (user: { email: string; first_name?: string; last_name?: string; display_name?: string }) => {
    if (user.display_name) return user.display_name;
    if (user.first_name || user.last_name) {
      return `${user.first_name || ""} ${user.last_name || ""}`.trim();
    }
    return user.email;
  };

  const handleSubmit = async () => {
    if (!selectedUserId) {
      setError("Veuillez sélectionner un utilisateur");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await createMembership({
        organization_id: organizationId,
        user_id: selectedUserId,
        org_role: role,
        status,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'ajout du membre");
    } finally {
      setSaving(false);
    }
  };

  const selectedUser = users.find((user) => user.id === selectedUserId);
  const roleInfo = getRoleDescription(role);

  return (
    <Modal
      title="Ajouter un membre"
      open={open}
      onClose={onClose}
      showDefaultButtons={false}
      showCloseButton={true}
      size="lg"
    >
      <div className="space-y-4">
        {/* User searchable dropdown */}
        <div>
          <label className="text-xs text-muted mb-2 block">Utilisateur</label>
          <div className="relative">
            <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              type="text"
              placeholder="Rechercher un utilisateur par nom ou email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-surface text-sm text-text placeholder:text-muted"
            />
          </div>
          {filteredUsers.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-border bg-surface">
              {filteredUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => setSelectedUserId(user.id)}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm transition",
                    selectedUserId === user.id
                      ? "bg-primary/10 text-primary"
                      : "text-text hover:bg-surface-muted"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{getUserDisplayName(user)}</div>
                      <div className="text-xs text-muted">{user.email}</div>
                    </div>
                    {selectedUserId === user.id && (
                      <span className="text-primary">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          {searchQuery && filteredUsers.length === 0 && !loading && (
            <div className="mt-2 text-xs text-muted text-center py-2">Aucun utilisateur trouvé</div>
          )}
          {!loading && users.length === 0 && (
            <div className="mt-2 text-xs text-muted text-center py-2">Aucun utilisateur disponible</div>
          )}
          {loading && (
            <div className="mt-2 text-xs text-muted text-center py-2">Chargement...</div>
          )}
        </div>

        {selectedUser && (
          <>
            {/* Rôle */}
            <div>
              <label className="text-xs text-muted mb-2 block">Rôle</label>
              <select
                className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {ROLE_OPTIONS.map((r) => {
                  const roleInfo = getRoleDescription(r);
                  return (
                    <option key={r} value={r}>
                      {roleInfo.label}
                    </option>
                  );
                })}
              </select>
              {roleInfo.description && (
                <div className="mt-1 text-xs text-muted">{roleInfo.description}</div>
              )}
            </div>

            {/* Statut */}
            <div>
              <label className="text-xs text-muted mb-2 block">Statut</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStatus("active")}
                  className={cn(
                    "flex-1 px-3 py-2 rounded-md text-sm font-medium transition",
                    status === "active"
                      ? "bg-success/10 text-success border border-success/20"
                      : "bg-surface-muted text-text border border-border"
                  )}
                >
                  Actif
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("suspended")}
                  className={cn(
                    "flex-1 px-3 py-2 rounded-md text-sm font-medium transition",
                    status === "suspended"
                      ? "bg-warning/10 text-warning border border-warning/20"
                      : "bg-surface-muted text-text border border-border"
                  )}
                >
                  Suspendu
                </button>
              </div>
            </div>
          </>
        )}

        {error && (
          <div className="rounded-md border border-danger bg-danger/10 p-3">
            <div className="text-xs text-danger">{error}</div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button label="Annuler" intent="default" size="sm" onClick={onClose} disabled={saving} />
          <Button
            label="Ajouter le membre"
            intent="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={saving || !selectedUserId}
          />
        </div>
      </div>
    </Modal>
  );
}
